/**
 * CihAtAGlanceCard — metadata card rendered beside the hero title on CIH
 * entity detail pages. Shows a short list of key/value rows. Rows with
 * null/empty values are skipped. Optionally renders social links as a
 * bottom row of icons.
 *
 * Aliases: at a glance, metadata card, hero meta card
 */

import type { ReactNode } from "react";

import { SafeLink } from "~/components/ui/safe-link";
import { cn } from "~/lib/cn";
import { isExternalHref } from "~/lib/shared/link-rel";

export type AtAGlanceRow = {
  readonly key: string;
  readonly value: string | null;
  readonly href?: string;
};

export type SocialLink = {
  readonly key: string;
  readonly href: string;
  readonly label: string;
  readonly icon: ReactNode;
};

type Props = {
  readonly label?: string;
  readonly rows: ReadonlyArray<AtAGlanceRow>;
  readonly socials?: ReadonlyArray<SocialLink>;
  readonly className?: string;
};

export function CihAtAGlanceCard({
  label = "At a glance",
  rows,
  socials,
  className,
}: Props) {
  const visible = rows.filter(
    (r): r is AtAGlanceRow & { value: string } => !!r.value,
  );
  const hasSocials = socials && socials.length > 0;
  if (visible.length === 0 && !hasSocials) return null;

  return (
    <div
      className={cn(
        "rounded-xl border border-gray-200 bg-white px-5 py-5",
        className,
      )}
    >
      <div className="text-[10px] font-medium tracking-[0.22em] text-gray-400 uppercase">
        {label}
      </div>
      {visible.length > 0 ? (
        <dl className="mt-3">
          {visible.map(({ key: rowKey, value, href }) => (
            <div
              key={rowKey}
              className="flex items-baseline justify-between gap-4 border-b border-gray-100 py-2.5 last:border-b-0"
            >
              <dt className="text-xs text-gray-500">{rowKey}</dt>
              <dd className="text-right text-xs font-medium text-gray-900">
                {href ? (
                  <SafeLink href={href} className="hover:text-purple-700">
                    {value}
                    {isExternalHref(href) ? (
                      <>
                        <span aria-hidden="true"> ↗</span>
                        <span className="sr-only"> (opens in new tab)</span>
                      </>
                    ) : null}
                  </SafeLink>
                ) : (
                  value
                )}
              </dd>
            </div>
          ))}
        </dl>
      ) : null}
      {hasSocials ? (
        <div
          className={cn(
            "flex items-center gap-3",
            visible.length > 0 ? "mt-4 border-t border-gray-100 pt-4" : "mt-3",
          )}
        >
          {socials.map(({ key, href, label: linkLabel, icon }) => (
            <SafeLink
              key={key}
              href={href}
              className="text-gray-400 transition-colors hover:text-purple-700"
            >
              {icon}
              <span className="sr-only">{linkLabel}</span>
            </SafeLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}
